import React from "react";
import { Text, Box } from "@showitinc/showit-wc";
import cx from "classnames";
import uiPrefix from "../../../utils/UiPrefixUtils";
import styles from "./ElementPropertiesLocking.module.css";

// Import the horizontal locking icons from public directory
const horizontalLockingIcons = "/horizontal-locking-icons.svg";

const HORIZ_LOCK = "locking-horizontal-";
const HORIZ_LOCK_LEFT = "l";
const HORIZ_LOCK_LEFT_STRETCH = "ls";
const HORIZ_LOCK_RIGHT = "r";
const HORIZ_LOCK_RIGHT_STRETCH = "rs";
const HORIZ_LOCK_STRETCH = "s";

/**
 * Renders the horizontal locking controls for element properties.
 *
 * @component
 * @param {Object} props - The component props.
 * @param {boolean} props.isLocked - Indicates if the element is locked.
 * @param {Function} props.clickLocking - Callback function to handle locking click events.
 * @param {Function} props.checked - Function to determine if a locking option is checked.
 * @param {string} props.elemLockH - Current horizontal locking state of the element.
 * @param {boolean} props.disabled - If the element is disabled
 * @param {boolean} props.allowStretch - If the element has stretch options
 *
 * @returns {JSX.Element} A div containing horizontal locking controls.
 *
 * @description
 * This component displays a set of buttons for controlling the horizontal locking of an element.
 * It includes options for locking to left, right, stretching from left, stretching from right,
 * and full horizontal stretch.
 *
 * @example
 * <ElementPropertiesHorizontalLocking
 *   isLocked={false}
 *   clickLocking={(type, value) => handleLocking(type, value)}
 *   checked={(current, value) => current === value ? 'checked' : ''}
 *   elemLockH="l"
 * />
 *
 */

const ElementPropertiesHorizontalLocking = ({
  isLocked,
  clickLocking,
  checked,
  elemLockH,
  disabled,
  allowStretch = true,
}) => (
  <>
    <Text className="panel-subheading">Horizontal Locking</Text>
    <Box className={`${isLocked && styles["disabled-button"]} ${styles["h-icons-grid"]}`} mb={4}>
      <div
        data-e2e={uiPrefix(`${HORIZ_LOCK}left-btn`)}
        className={`${uiPrefix(`${HORIZ_LOCK}left-btn`)} ${styles["locking-button"]}  ${checked(
          elemLockH,
          HORIZ_LOCK_LEFT
        )}
          ${disabled && styles["disabled-button"]}
        `}
        onClick={() => clickLocking("lockH", HORIZ_LOCK_LEFT)}
      >
        <svg className={cx([styles["lock-to-left"], styles["svg-icon"]])}>
          <use href={`${horizontalLockingIcons}#locktoleft`} />
        </svg>
      </div>
      <div
        data-e2e={uiPrefix(`${HORIZ_LOCK}right-btn`)}
        className={`${uiPrefix(`${HORIZ_LOCK}right-btn`)} ${styles["locking-button"]} ${checked(
          elemLockH,
          HORIZ_LOCK_RIGHT
        )}
        ${disabled && styles["disabled-button"]}
        `}
        onClick={() => clickLocking("lockH", HORIZ_LOCK_RIGHT)}
      >
        <svg className={cx([styles["lock-to-right"], styles["svg-icon"]])}>
          <use href={`${horizontalLockingIcons}#locktoright`} />
        </svg>
      </div>
      {allowStretch && (
        <>
          <div
            data-e2e={uiPrefix(`${HORIZ_LOCK}stretch-left-btn`)}
            className={`${uiPrefix(`${HORIZ_LOCK}stretch-left-btn`)} ${
              styles["locking-button"]
            } ${checked(elemLockH, HORIZ_LOCK_LEFT_STRETCH)}
        ${disabled && styles["disabled-button"]}
        `}
            onClick={() => clickLocking("lockH", HORIZ_LOCK_LEFT_STRETCH)}
          >
            <svg className={cx([styles["stretch-left"], styles["svg-icon"]])}>
              <use href={`${horizontalLockingIcons}#stretchtoleft`} />
            </svg>
          </div>
          <div
            data-e2e={uiPrefix(`${HORIZ_LOCK}stretch-right-btn`)}
            className={`${uiPrefix(`${HORIZ_LOCK}stretch-right-btn`)} ${
              styles["locking-button"]
            } ${checked(elemLockH, HORIZ_LOCK_RIGHT_STRETCH)}
        ${disabled && styles["disabled-button"]}
        `}
            onClick={() => clickLocking("lockH", HORIZ_LOCK_RIGHT_STRETCH)}
          >
            <svg className={cx([styles["stretch-right"], styles["svg-icon"]])}>
              <use href={`${horizontalLockingIcons}#stretchtoright`} />
            </svg>
          </div>
          <div
            data-e2e={uiPrefix(`${HORIZ_LOCK}stretch-full-btn`)}
            className={`${uiPrefix(`${HORIZ_LOCK}stretch-full-btn`)}  ${styles["locking-button"]} ${
              styles["full-stretch"]
            } ${checked(elemLockH, HORIZ_LOCK_STRETCH)}
        ${disabled && styles["disabled-button"]}
        `}
            onClick={() => clickLocking("lockH", HORIZ_LOCK_STRETCH)}
          >
            <svg className={cx([styles["full-stretch"], styles["svg-icon"], styles.full])}>
              <use href={`${horizontalLockingIcons}#stretchfull`} />
            </svg>
          </div>
        </>
      )}
    </Box>
  </>
);
export default ElementPropertiesHorizontalLocking;
